import { useEffect, useState } from 'react';

/**
 * Hook that debounces a value.
 *
 * Returns the passed value only after it stayed unchanged for `delay`
 * milliseconds. Used for screen reader live regions: writing to a polite
 * live region on every keystroke queues one announcement per character,
 * which makes typing unusable with a screen reader.
 *
 * @param {T} value - value to debounce
 * @param {number} delay - delay in milliseconds
 *
 * @returns {T} latest value that stayed unchanged for the given delay
 */
export function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedValue(value), delay);

    return () => clearTimeout(timeout);
  }, [value, delay]);

  return debouncedValue;
}
